class ProductGallerySlider extends HTMLElement {
  constructor() {
    super();
    this._pointerStart = null;
    this._dragThreshold = 8;
    this._isDragging = false;
    this._suppressZoomClick = false;

    const showButtons = window.matchMedia("(min-width: 768px)").matches;
    let options = {
      imagesLoaded: true,
      cellSelector: ".product-gallery-slide",
      contain: true,
      prevNextButtons: showButtons,
      fade: true,
      wrapAround: false
    };

    const slider = new Flickity(this, options);

    const modelOverlay = this.querySelector(".product-gallery-slider-model-overlay");
    const modelEntries = modelOverlay
      ? modelOverlay.querySelector("[data-model-entries]")
      : null;

    this.addEventListener("pointerdown", (event) => {
      const cell = event.target.closest(".flickity-slider > div");

      if (!cell || !this.contains(cell)) {
        return;
      }

      this._pointerStart = {
        x: event.clientX,
        y: event.clientY
      };
      this._isDragging = false;
    });

    this.addEventListener("pointermove", (event) => {
      if (!this._pointerStart) {
        return;
      }

      const deltaX = Math.abs(event.clientX - this._pointerStart.x);
      const deltaY = Math.abs(event.clientY - this._pointerStart.y);

      if (deltaX > this._dragThreshold || deltaY > this._dragThreshold) {
        this._isDragging = true;
      }
    });

    this.addEventListener("pointerup", () => {
      if (this._isDragging) {
        this._suppressZoomClick = true;

        requestAnimationFrame(() => {
          this._suppressZoomClick = false;
        });
      }

      this._pointerStart = null;
    });

    this.addEventListener("pointercancel", () => {
      this._pointerStart = null;
      this._isDragging = false;
    });

    this.addEventListener("click", (event) => {
      if (this._suppressZoomClick) {
        return;
      }

      if (event.target.closest(".flickity-button, .flickity-page-dots")) {
        return;
      }

      const cell = event.target.closest(".flickity-slider > div");

      if (!cell || !this.contains(cell)) {
        return;
      }

      if (!cell.classList.contains("is-selected")) {
        return;
      }

      document.dispatchEvent(new CustomEvent("openZoom:click"));
    });

    slider.on("change", (selected_index) => {
      play_and_pause_videos(selected_index);
      updateModelOverlay(selected_index);
    });

    document.addEventListener("productgallerythumbnails:click", (event) => {
      slider.select(event.detail.index);
    });

    document.addEventListener("productformvariants:change", (event) => {
      slider.cells.forEach((cell, index) => {
        if (cell.element.dataset.variantId?.includes(event.detail.variant.id)) {
          slider.select(index);
        }
      });
    });

    if (Theme.URLParams.has("variant")) {
      slider.cells.forEach((cell, index) => {
        if (cell.element.dataset.variantId?.includes(Theme.URLParams.get("variant"))) {
          slider.select(index);
        }
      });
    }

    function updateModelOverlay(selected_index) {
      if (!modelOverlay || !modelEntries) {
        return;
      }

      const hasMultipleModelSizes = modelOverlay.getAttribute("data-model-sizes") === "true";

      if (!hasMultipleModelSizes) {
        return;
      }

      const cells = slider.getCellElements();
      const active = cells && cells[selected_index] ? cells[selected_index] : null;
      const tags = active
        ? String(active.getAttribute("data-model") || "").trim().split(/\s+/).filter(Boolean)
        : [];

      const sizes = tags
        .map((tag) => modelOverlay.getAttribute(`data-model-size-${tag.replace(/\D/g, "")}`))
        .filter(Boolean);

      if (!sizes.length) {
        modelOverlay.setAttribute("hidden", "");
        modelOverlay.style.display = "none";
        return;
      }

      modelOverlay.removeAttribute("hidden");
      modelOverlay.style.display = "";
      modelOverlay.classList.toggle("state--multiple", sizes.length > 1);

      modelEntries.innerHTML = sizes
        .map((size, index) => buildEntry(size, sizes.length > 1, index === sizes.length - 1))
        .join("");
    }

    function buildEntry(value, isMultiple, isLast) {
      const parts = String(value || "").trim().split(/\s+/);
      const name = parts.length > 2 ? parts.slice(0, 2).join(" ") : "";
      let sizeText = parts.length > 2 ? parts.slice(2).join(" ") : String(value || "").trim();

      if (!isLast) {
        sizeText = `${sizeText},&nbsp;`;
      }

      const nameClasses = isMultiple ? "" : "visibility--desktop";
      const nameMarkup = name
        ? `<span class="${nameClasses}" data-model-name>${name}&nbsp;</span>`
        : "";

      return `<span class="flex" data-model-entry>${nameMarkup}<span data-model-size-text>${sizeText}</span></span>`;
    }

    function play_and_pause_videos(selected_index) {
      slider.getCellElements().forEach((element, element_index) => {
        const video = element.querySelector("video");

        if (!video) {
          return;
        }

        if (selected_index == element_index) {
          video.play();
        } else {
          video.pause();
        }
      });
    }

    updateModelOverlay(slider.selectedIndex || 0);
  }
}

customElements.define("product-gallery-slider", ProductGallerySlider);

class ProductGalleryThumbnails extends HTMLElement {
  constructor() {
    super();

    this.addEventListener("click", (event) => {
      event.preventDefault();

      const element = event.target.closest("a");

      if (!element) return;

      this.querySelectorAll("a.is-active").forEach((link) => {
        link.classList.remove("is-active");
      });

      element.classList.add("is-active");

      document.dispatchEvent(new CustomEvent("productgallerythumbnails:click", {
        detail: {
          index: element.dataset.index
        }
      }));
    });
  }
}


customElements.define("product-gallery-thumbnails", ProductGalleryThumbnails);

class ProductGalleryZoom extends Theme.Component {
  connectedCallback() {
    super.connectedCallback();
    this.addDirectEvent("openZoom:click", document, this.openZoom.bind(this));
    this.addDirectEvent("pointermove", this, this.onPointerMove.bind(this));
    this.addDirectEvent("pointerleave", this, this.onPointerLeave.bind(this));
    this.addDirectEvent("click", this, this.onSuperZoomClick.bind(this));
    this.addDirectEvent("scroll", this, Theme.Helpers.throttle(this.onScroll.bind(this), 100));
  }

  openZoom() {
    this.classList.add("is-active");
    this.updateActiveThumbnailByScroll();
  }

  closeZoom() {
    this.classList.remove("is-active");
    this.classList.remove("is-super-zoom");
    this.resetSuperZoom();
  }

  onSuperZoomClick(event) {
    const control = event.target.closest(".product-gallery-zoom-thumbnails, .product-gallery-zoom-close");

    if (control) {
      return;
    }

    const item = event.target.closest("[data-zoom-item]");

    if (!item || !this.contains(item)) {
      return;
    }

    if (this.classList.contains("is-super-zoom")) {
      this.classList.remove("is-super-zoom");
      this.resetSuperZoom();
      return;
    }

    this.classList.add("is-super-zoom");
    this.setSuperZoomOrigin(event, item);
  }

  onPointerMove(event) {
    if (!this.classList.contains("is-super-zoom")) {
      return;
    }

    const item = event.target.closest("[data-zoom-item]");

    if (!item || !this.contains(item)) {
      return;
    }

    this.setSuperZoomOrigin(event, item);
  }

  setSuperZoomOrigin(event, item) {
    const image = item.querySelector("[data-zoom-image]");

    if (!image) {
      return;
    }

    const rect = item.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;

    this.querySelectorAll("[data-zoom-item].is-super-zoom-active").forEach((active_item) => {
      if (active_item !== item) {
        active_item.classList.remove("is-super-zoom-active");
      }
    });

    item.classList.add("is-super-zoom-active");
    image.style.transformOrigin = `${x}% ${y}%`;
  }

  onPointerLeave() {
    if (!this.classList.contains("is-super-zoom")) {
      return;
    }

    this.resetSuperZoom();
  }

  resetSuperZoom() {
    this.querySelectorAll("[data-zoom-item].is-super-zoom-active").forEach((item) => {
      item.classList.remove("is-super-zoom-active");
    });

    this.querySelectorAll("[data-zoom-image]").forEach((image) => {
      image.style.transformOrigin = "50% 50%";
    });
  }

  scrollToImage(detail, element) {
    const thumbnail = element;
    const index = thumbnail.dataset.index;
    this.setActiveThumbnail(index);
    this._isThumbnailScroll = true;

    const target = this.querySelector(`:scope > .stack--3 [data-index="${index}"]`);

    if (!target) {
      return;
    }

    target.scrollIntoView({
      behavior: "smooth",
      block: "start"
    });
  }

  onScroll() {
    if (this._isThumbnailScroll) {
      clearTimeout(this._thumbnailScrollTimeout);
      this._thumbnailScrollTimeout = setTimeout(() => {
        this._isThumbnailScroll = false;
      }, 250);
      return;
    }
    this.updateActiveThumbnailByScroll();
  }

  updateActiveThumbnailByScroll() {
    const items = Array.from(this.querySelectorAll(":scope > .stack--3 [data-index]"));

    if (!items.length) {
      return;
    }

    const viewportCenter = this.scrollTop + (this.clientHeight / 2);
    let activeItem = items[0];
    let smallestDistance = Infinity;

    items.forEach((item) => {
      const itemCenter = item.offsetTop + (item.offsetHeight / 2);
      const distance = Math.abs(itemCenter - viewportCenter);

      if (distance < smallestDistance) {
        smallestDistance = distance;
        activeItem = item;
      }
    });

    this.setActiveThumbnail(activeItem.dataset.index);
  }

  setActiveThumbnail(index) {
    const thumbnails = this.querySelectorAll(".product-gallery-zoom-thumbnails [data-index]");

    thumbnails.forEach((thumbnail) => {
      thumbnail.classList.toggle("is-active", thumbnail.dataset.index === String(index));
    });
  }
  
}

customElements.define("product-gallery-zoom", ProductGalleryZoom);